/**
 * Reach runtime contracts (M3 plan §2.6.3). FROZEN after R1: later tasks may add fields or exports, never rename
 * or remove them.
 *
 * `ReachCore` is the headless part (runs in Node): galaxy, state, session, events, the per-subsystem state objects,
 * ports, rng, input, subsystems, `step(sim)` and `command(cmd)`. `ReachRuntime` is the core plus the browser parts
 * (environment look, scaled pass, model preload, storage, dialogs, tuning groups, the services registry).
 * Per-tick paths (`step`, subsystem `step`, `InteractableProvider.nearest`, `controlFilter`) allocate nothing (D-014).
 */
import type { PerspectiveCamera, Scene, WebGPURenderer } from 'three/webgpu'
import type { ColliderData } from '../../assets/extract'
import type { AssetLoader, LoadedModel } from '../../assets/loader'
import type { EventRing } from '../../combat/events'
import type { Rng } from '../../core/rng'
import type { GameData } from '../../data'
import type { AssetsFile, SocketEntry } from '../../data/assetSchema'
import type { QuatT, SystemLayout, Vec3T } from '../../galaxy/layout/types'
import type { Galaxy } from '../../galaxy/types'
import type { PlayerControl } from '../../input/playerInput'
import type { StaticTrimesh } from '../../physics/statics'
import type { FactionId, TuningGroup } from '../../render/contracts'
import type { Environment } from '../../render/environment'
import type { MaterialLibrary } from '../../render/materials'
import type { Sim } from '../../sim'
import type { ReachInputState } from '../input'
import type { CareerPort, EncounterPort, StationServiceRegistry } from '../ports'
import type { NavTargetRef, ReachCommand, ReachCommandResult, ReachLocation, ReachSave, ReachState } from '../types'

export type TravelMode = 'normal' | 'charging' | 'cruise' | 'lane' | 'interdicted' | 'transit'
export type MassLockReason = 'none' | 'station' | 'relay' | 'body' | 'ship' | 'weaponLock'
/** `MassLockReason` in event order (the `b` field of `REV.massLockDenied`). */
export const MASS_LOCK_REASONS: readonly MassLockReason[] = ['none', 'station', 'relay', 'body', 'ship', 'weaponLock']
export interface TravelState {
  mode: TravelMode; chargeS: number; cooldownS: number; massLock: MassLockReason; massLockIndex: number
  /** The flown hull's charge hold (s) this tick (M10 P5a7a, `cruiseChargeSFor`); 0 before the first normal tick (the HUD reads the global). */
  chargeNeedS: number
  throttle: number; speed: number; allowed: number; targetDistance: number; etaS: number
  laneIndex: number; laneExitRing: number
  interdiction: { active: boolean; escape: number; pull: number; ex: number; ey: number; ez: number; timeLeftS: number }
  transit: { phase: 'none' | 'enter' | 'tunnel' | 'exit'; t: number; destSystemIndex: number; ready: boolean }
  relayHint: { relayIndex: number; distance: number; aligned: boolean; slowEnough: boolean; online: boolean }
}
export interface ScanState { holdS: number; blocked: 'none' | 'target' | 'combat' | 'travel'; lastPingAt: number; lastRevealed: number }
export interface RelinkState {
  /** `restart`: M7 S40 part 3, a derelict restart defence (story-link/reactivation/defence.ts; `relayIndex` −1). */
  relayIndex: number; phase: 'idle' | 'reboot' | 'restart'; t: number; duration: number
  hull: number; hullMax: number; wave: number; waves: number; objectiveEid: number
}
export interface NavState { target: NavTargetRef | null; x: number; y: number; z: number; distance: number; routeNextRelayIndex: number }
export type InteractionKind = 'none' | 'hail' | 'deliverParts' | 'beginReboot' | 'repairLaneRing' | 'openCache' | 'readBeacon'
  // M7 S40: a surveyed derelict's repair port (story-link/reactivation/live.ts); the index is the layout POI index.
  | 'reactVent' | 'reactFence' | 'reactHail' | 'reactDeliver' | 'reactRestart' | 'reactFight'
  // M7 S43: register a lit restart (the Registrant choice); deliver the Candle's supplies at the Harrow.
  | 'reactRegister' | 'reactCandle'
  // M9 CH-12a: a hulk's next step (hulks/live.ts); the index is the live hulk list's index.
  | 'hulkVent' | 'hulkFence' | 'hulkFight' | 'hulkStart' | 'hulkDeliver' | 'hulkClaim'
export interface InteractionPrompt { kind: InteractionKind; index: number; distance: number; blocked: string | null }
export type UiRequest =
  | { kind: 'services'; stationId: string } | { kind: 'sponsor'; relayId: string } | { kind: 'confirmReboot'; relayId: string }
export interface ActiveStructure {
  kind: 'station' | 'relay' | 'laneRing' | 'poi'; index: number; id: string; assetId: string | null
  position: Vec3T; rotation: QuatT; seed: number; look: FactionId
  state: number // relay 0 offline, 1 online, 2 reboot; lane ring 0 down, 1 up; poi 0 intact, 1 looted/cleared
  lightScale?: number // M5 C15: a station's light scale (a war-disabled station goes dark); absent = 1
}
export interface SystemLoadTimings { layoutMs: number; hooks: Record<string, number>; streamMs: number; totalMs: number }
export type ArrivalSpec =
  | { kind: 'relay'; relayId: string } | { kind: 'station'; stationId: string }
  | { kind: 'location'; location: ReachLocation } | { kind: 'home' } | { kind: 'respawn' }
export type ReachSpawnKind = 'interdictor' | 'baseDefender' | 'rebootRaider' | 'guardian' | 'patrol' | 'ally'
export interface SpawnShipSpec { def: string; profile: 'rookie' | 'veteran' | 'ace'; count: number }
export interface SpawnGroupSpec {
  ships: readonly SpawnShipSpec[]; center: Vec3T; facing: Vec3T; radiusM: readonly [number, number]
  team: number; kind: ReachSpawnKind; despawnM: number
}
export interface SystemSession {
  readonly layout: SystemLayout | null; readonly ready: boolean; readonly timings: SystemLoadTimings | null
  readonly structures: readonly ActiveStructure[]; readonly structuresVersion: number
  readonly fieldKeys: readonly string[] // parallel to sim.field's rocks (rock keys, R7)
  enterSystem(systemId: string, arrival: ArrivalSpec): Promise<SystemLoadTimings>
  addEnterHook(name: string, hook: (layout: SystemLayout) => Promise<void> | void): void
  spawnGroup(spec: SpawnGroupSpec): number // group id
  groupAlive(group: number): number
  despawnGroup(group: number): void
  setStructureState(kind: ActiveStructure['kind'], index: number, state: number): void
  provideDerelictCollider(poiId: string, trimesh: StaticTrimesh, position: Vec3T, rotation: QuatT): void
}
export interface InteractableProvider {
  /** Writes this provider's nearest valid prompt into `out`; false when there is none. Allocation-free. */
  nearest(core: ReachCore, sim: Sim, out: InteractionPrompt): boolean
  activate(core: ReachCore, sim: Sim, prompt: InteractionPrompt): ReachCommandResult
}
export interface ReachSubsystem {
  readonly name: string; readonly order: number
  init(core: ReachCore): void
  step(core: ReachCore, sim: Sim): void // allocation-free
  /** Once per frame while a hold (docked) stops the sim ticks, so `step` does not run; allocation-free. */
  held?(core: ReachCore, sim: Sim): void
  onSystemEnter?(core: ReachCore, sim: Sim, layout: SystemLayout): void
  onSystemLeave?(core: ReachCore, sim: Sim): void
  readonly commands?: {
    readonly [K in ReachCommand['kind']]?: (core: ReachCore, sim: Sim, cmd: Extract<ReachCommand, { kind: K }>) => ReachCommandResult
  }
  readonly interactables?: readonly InteractableProvider[]
  readonly controlFilter?: (core: ReachCore, c: PlayerControl) => void
  readonly tuning?: TuningGroup
}
export interface ReachCore {
  readonly galaxy: Galaxy; readonly state: ReachState; readonly session: SystemSession
  readonly events: EventRing; readonly travel: TravelState; readonly scan: ScanState; readonly relink: RelinkState
  readonly nav: NavState; readonly interaction: InteractionPrompt; uiRequest: UiRequest | null
  readonly input: ReachInputState; readonly career: CareerPort; readonly encounters: EncounterPort; readonly rng: Rng
  readonly subsystems: readonly ReachSubsystem[]; readonly colliders: StructureColliderSource
  layoutOf(systemId: string): SystemLayout // cached
  step(sim: Sim): void
  command(cmd: ReachCommand): ReachCommandResult
}
export interface StructureColliderSource {
  ensure(assetIds: readonly string[]): Promise<void>
  colliderOf(assetId: string): ColliderData | null
  socketsOf(assetId: string): readonly SocketEntry[]
}
/** What the browser runtime and views load models through; R2's ModelCache satisfies it structurally. */
export interface ModelSource extends StructureColliderSource { get(id: string): LoadedModel | undefined }
export interface ReachCoreDeps {
  sim: Sim; data: GameData; galaxySeed: number; state?: ReachState
  subsystems?: readonly ReachSubsystem[] // default: discoverSubsystems()
  colliders: StructureColliderSource; career?: CareerPort; encounters?: EncounterPort; rngSeed?: number
}
export interface ReachRuntime extends ReachCore {
  readonly tuning: TuningGroup[]; readonly services: StationServiceRegistry; readonly models: ModelSource
  readonly subtitle: string; readonly cameraFar: number
  persist(reason: 'auto' | 'manual'): boolean
  toSave(): ReachSave
  dispose(): void
}
/** `localStorage`, or a stand-in in tests (`memoryStorage()` in src/reach/testing/fakes.ts). */
export type StorageLike = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>
export interface ReachRuntimeDeps {
  sim: Sim; data: GameData; renderer: WebGPURenderer; scene: Scene; camera: PerspectiveCamera
  env: Environment; lib: MaterialLibrary; loader: AssetLoader; assets: AssetsFile
  overlayRoot: HTMLElement; search: string; storage: StorageLike | null
}
// Exports of src/reach/runtime/index.ts: createReachCore(deps), createReachRuntime(deps), discoverSubsystems().
